import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ExhibitionHallManager = () => {
  const [allInstitutes, setAllInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInstitutes();
  }, []);

  const loadInstitutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        ?.from('institute_profiles')
        ?.select('id, institute_name, logo_url, approval_status, in_exhibition_hall, whatsapp_enabled, categories, state, booth_type, user:user_id(email, city, state)')
        ?.eq('approval_status', 'approved')
        ?.order('institute_name', { ascending: true });
      if (err) throw err;
      setAllInstitutes(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExhibitionHall = async (institute) => {
    const newValue = !institute?.in_exhibition_hall;
    try {
      setSaving(institute?.id);
      const { error: err } = await supabase
        ?.from('institute_profiles')
        ?.update({ in_exhibition_hall: newValue })
        ?.eq('id', institute?.id);
      if (err) throw err;
      setAllInstitutes(prev =>
        prev?.map(i => i?.id === institute?.id ? { ...i, in_exhibition_hall: newValue } : i)
      );
    } catch (err) {
      alert(err?.message || 'Failed to update exhibition hall status');
    } finally {
      setSaving(null);
    }
  };

  const handleToggleWhatsApp = async (institute) => {
    const newValue = !institute?.whatsapp_enabled;
    try {
      setSaving(`wa_${institute?.id}`);
      const { error: err } = await supabase
        ?.from('institute_profiles')
        ?.update({ whatsapp_enabled: newValue })
        ?.eq('id', institute?.id);
      if (err) throw err;
      setAllInstitutes(prev =>
        prev?.map(i => i?.id === institute?.id ? { ...i, whatsapp_enabled: newValue } : i)
      );
    } catch (err) {
      alert(err?.message || 'Failed to update WhatsApp status');
    } finally {
      setSaving(null);
    }
  };

  const exhibitionInstitutes = allInstitutes?.filter(i => i?.in_exhibition_hall);
  const filtered = allInstitutes?.filter(i =>
    i?.institute_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 mb-3">{error}</p>
        <Button onClick={loadInstitutes} iconName="RefreshCw" iconPosition="left" size="sm">Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Exhibition Hall Manager</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Select which approved institutes appear in the Exhibition Hall. Toggle to add or remove institutes.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-4 py-2 rounded-full">
          <Icon name="Building2" size={16} color="#2563eb" />
          <span className="text-sm font-bold text-blue-700">{exhibitionInstitutes?.length} in Hall</span>
        </div>
      </div>

      {/* Currently in Exhibition Hall */}
      {exhibitionInstitutes?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">Currently in Exhibition Hall</p>
          <div className="flex flex-wrap gap-2">
            {exhibitionInstitutes?.map(i => (
              <div key={i?.id} className="flex items-center gap-2 bg-white border border-blue-300 rounded-full px-3 py-1.5 text-sm">
                <Icon name="Building2" size={12} color="#2563eb" />
                <span className="font-medium text-foreground">{i?.institute_name}</span>
                <button
                  onClick={() => handleToggleExhibitionHall(i)}
                  className="text-muted-foreground hover:text-red-500 transition-colors ml-1"
                  disabled={saving === i?.id}
                  title="Remove from Exhibition Hall"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {exhibitionInstitutes?.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Icon name="AlertTriangle" size={20} color="#d97706" />
          <p className="text-sm text-yellow-800">
            No institutes are currently in the Exhibition Hall. Toggle institutes below to add them.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          placeholder="Search institutes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Institute list */}
      <div className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
        {filtered?.map(institute => (
          <div
            key={institute?.id}
            className={`bg-white border rounded-xl p-4 transition-all ${
              institute?.in_exhibition_hall ? 'border-blue-300 bg-blue-50/30' : 'border-border'
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Logo */}
              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={institute?.logo_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png'}
                  alt={`${institute?.institute_name} logo`}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-foreground text-sm">{institute?.institute_name}</h3>
                  {institute?.in_exhibition_hall && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">
                      <Icon name="Building2" size={10} color="#2563eb" /> In Hall
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  {(institute?.state || institute?.user?.state) && (
                    <span className="flex items-center gap-1">
                      <Icon name="MapPin" size={11} /> {institute?.state || institute?.user?.state}
                    </span>
                  )}
                  {institute?.user?.email && (
                    <span className="flex items-center gap-1">
                      <Icon name="Mail" size={11} /> {institute?.user?.email}
                    </span>
                  )}
                  {institute?.categories?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Icon name="Tag" size={11} /> {institute?.categories?.slice(0, 2)?.join(', ')}
                      {institute?.categories?.length > 2 && ` +${institute?.categories?.length - 2}`}
                    </span>
                  )}
                  <span className="capitalize text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    {institute?.booth_type || 'free'}
                  </span>
                </div>
              </div>

              {/* Toggle */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs font-medium ${institute?.in_exhibition_hall ? 'text-blue-600' : 'text-muted-foreground'}`}>
                  {institute?.in_exhibition_hall ? 'In Hall' : 'Not in Hall'}
                </span>
                <button
                  onClick={() => handleToggleExhibitionHall(institute)}
                  disabled={saving === institute?.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
                    institute?.in_exhibition_hall ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  title={institute?.in_exhibition_hall ? 'Remove from Exhibition Hall' : 'Add to Exhibition Hall'}
                >
                  {saving === institute?.id ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                    </span>
                  ) : (
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      institute?.in_exhibition_hall ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  )}
                </button>
                <span className={`text-xs font-medium ${institute?.whatsapp_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                  WhatsApp
                </span>
                <button
                  onClick={() => handleToggleWhatsApp(institute)}
                  disabled={saving === `wa_${institute?.id}`}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
                    institute?.whatsapp_enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  title={institute?.whatsapp_enabled ? 'Disable WhatsApp button' : 'Enable WhatsApp button'}
                >
                  {saving === `wa_${institute?.id}` ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                    </span>
                  ) : (
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      institute?.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}

        {filtered?.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Search" size={40} className="mx-auto mb-3 opacity-40" />
            <p>No institutes match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitionHallManager;
